import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, ClipboardCheck, Check, X, Plus, RefreshCw } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_LABELS = {
  part: "Peça",
  work_instruction: "Instrução de Trabalho",
  pcq_plan: "PCQ",
  mold: "Molde",
  machine: "Máquina",
};

const STATUS_META = {
  pending: { label: "Pendente", color: "bg-amber-100 text-amber-800 border-amber-300", icon: "⏳" },
  approved: { label: "Aprovado", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: "✓" },
  rejected: { label: "Rejeitado", color: "bg-rose-100 text-rose-800 border-rose-300", icon: "✗" },
};

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [decisionDialog, setDecisionDialog] = useState(null);
  const [decision, setDecision] = useState("approved");
  const [comments, setComments] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ entity_type: "part", entity_id: "", entity_code: "", entity_label: "", requested_notes: "", required_role: "quality" });
  const [parts, setParts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/approvals${tab !== "all" ? "?status=" + tab : ""}`);
      setItems(r.data || []);
    } catch (e) { showError(e, "Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => { axios.get(`${API}/parts`).then(r => setParts(r.data || [])).catch(() => {}); }, []);

  const submitDecision = async () => {
    try {
      await axios.post(`${API}/approvals/${decisionDialog.id}/decide`, { decision, comments });
      toast.success(`Aprovação ${decision === "approved" ? "aprovada" : "rejeitada"}`);
      setDecisionDialog(null); setComments("");
      load();
    } catch (e) { showError(e, "Erro"); }
  };

  const createRequest = async () => {
    if (!newForm.entity_id) { toast.error("Escolha uma entidade"); return; }
    try {
      await axios.post(`${API}/approvals`, newForm);
      toast.success("Pedido criado");
      setShowNew(false);
      setNewForm({ entity_type: "part", entity_id: "", entity_code: "", entity_label: "", requested_notes: "", required_role: "quality" });
      load();
    } catch (e) { showError(e, "Erro"); }
  };

  const selectedEntity = (id) => {
    const p = parts.find(x => x.id === id);
    if (p) return { code: p.code, label: p.description };
    return { code: "", label: "" };
  };

  return (
    <div className="p-6 space-y-4" data-testid="approvals-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-2"><ClipboardCheck className="h-7 w-7 text-indigo-600" />Aprovações Digitais</h1>
          <p className="text-sm text-muted-foreground mt-1">Workflow de aprovação de fichas técnicas e configurações antes de entrar em produção.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
          <Button onClick={() => setShowNew(true)} data-testid="btn-new-approval"><Plus className="h-4 w-4 mr-2" />Novo Pedido</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b flex gap-4">
        {[
          { key: "pending", label: "Pendentes" },
          { key: "approved", label: "Aprovadas" },
          { key: "rejected", label: "Rejeitadas" },
          { key: "all", label: "Todas" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`pb-2 text-sm font-semibold border-b-2 ${tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto py-8" /> : (
        <Card>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entidade</TableHead>
                  <TableHead>Pedido por</TableHead>
                  <TableHead>Data pedido</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Decisão</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Sem aprovações</TableCell></TableRow>}
                {items.map(a => {
                  const status = STATUS_META[a.status] || STATUS_META.pending;
                  return (
                    <TableRow key={a.id} data-testid={`approval-${a.id}`}>
                      <TableCell>
                        <div className="text-xs text-slate-500 uppercase font-bold">{ENTITY_LABELS[a.entity_type] || a.entity_type}</div>
                        <div className="font-semibold">{a.entity_code || "-"} · {a.entity_label || "-"}</div>
                        {a.requested_notes && <div className="text-xs text-slate-600 mt-1 italic">"{a.requested_notes}"</div>}
                      </TableCell>
                      <TableCell className="text-sm">{a.requested_by_name || a.requested_by}</TableCell>
                      <TableCell className="text-xs font-mono">{new Date(a.requested_at).toLocaleString("pt-PT")}</TableCell>
                      <TableCell>
                        <span className={`text-[10px] px-2 py-1 rounded border font-bold ${status.color}`}>{status.icon} {status.label}</span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {a.decided_by ? <>
                          <div>{a.decided_by_name || a.decided_by}</div>
                          <div className="text-slate-500">{new Date(a.decided_at).toLocaleString("pt-PT")}</div>
                          {a.decision_comments && <div className="italic text-slate-600 mt-1">"{a.decision_comments}"</div>}
                        </> : "—"}
                      </TableCell>
                      <TableCell>
                        {a.status === "pending" && (
                          <div className="flex gap-1">
                            <Button size="sm" onClick={() => { setDecisionDialog(a); setDecision("approved"); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`btn-decide-${a.id}`}>
                              <Check className="h-3 w-3 mr-1" />Decidir
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Decision Dialog */}
      <Dialog open={!!decisionDialog} onOpenChange={(v) => !v && setDecisionDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Decidir Aprovação</DialogTitle></DialogHeader>
          {decisionDialog && (
            <div className="space-y-3 mt-2">
              <div className="bg-slate-50 border rounded p-3">
                <div className="text-xs uppercase font-bold text-slate-500">{ENTITY_LABELS[decisionDialog.entity_type]}</div>
                <div className="font-semibold">{decisionDialog.entity_code || "-"} · {decisionDialog.entity_label || "-"}</div>
                {decisionDialog.requested_notes && <div className="text-xs text-slate-600 mt-2 italic">"{decisionDialog.requested_notes}"</div>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setDecision("approved")} className={`p-3 border-2 rounded font-bold ${decision === "approved" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200"}`} data-testid="decision-approve">
                  <Check className="h-5 w-5 mx-auto mb-1" />Aprovar
                </button>
                <button onClick={() => setDecision("rejected")} className={`p-3 border-2 rounded font-bold ${decision === "rejected" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200"}`} data-testid="decision-reject">
                  <X className="h-5 w-5 mx-auto mb-1" />Rejeitar
                </button>
              </div>
              <div>
                <Label>Comentários {decision === "rejected" && <span className="text-rose-600">*</span>}</Label>
                <Textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Observações da decisão" rows={3} data-testid="decision-comments" />
              </div>
              <Button className="w-full" onClick={submitDecision} disabled={decision === "rejected" && !comments} data-testid="submit-decision">Confirmar Decisão</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Request Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo Pedido de Aprovação</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Tipo de entidade</Label>
              <select className="w-full h-10 border rounded px-3 text-sm" value={newForm.entity_type} onChange={e => setNewForm({...newForm, entity_type: e.target.value})}>
                {Object.entries(ENTITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            {newForm.entity_type === "part" ? (
              <div>
                <Label>Peça</Label>
                <select className="w-full h-10 border rounded px-3 text-sm" value={newForm.entity_id} onChange={e => {
                  const ent = selectedEntity(e.target.value);
                  setNewForm({...newForm, entity_id: e.target.value, entity_code: ent.code, entity_label: ent.label});
                }} data-testid="new-approval-part">
                  <option value="">Selecione...</option>
                  {parts.map(p => <option key={p.id} value={p.id}>{p.code} - {p.description}</option>)}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div><Label>ID</Label><Input value={newForm.entity_id} onChange={e => setNewForm({...newForm, entity_id: e.target.value})} placeholder="uuid" /></div>
                <div><Label>Código</Label><Input value={newForm.entity_code} onChange={e => setNewForm({...newForm, entity_code: e.target.value})} placeholder="Código" /></div>
              </div>
            )}
            <div>
              <Label>Role que aprova</Label>
              <select className="w-full h-10 border rounded px-3 text-sm" value={newForm.required_role} onChange={e => setNewForm({...newForm, required_role: e.target.value})}>
                <option value="admin">Admin</option>
                <option value="quality">Qualidade</option>
                <option value="shift_leader">Chefe de Turno</option>
              </select>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea rows={2} value={newForm.requested_notes} onChange={e => setNewForm({...newForm, requested_notes: e.target.value})} />
            </div>
            <Button onClick={createRequest} className="w-full" data-testid="submit-new-approval">Criar Pedido</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
